import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Loader2, Pencil, Trash2, ShieldCheck, CalendarOff } from "lucide-react";
import { toast } from "sonner";

const initials = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");
const inputCls = "mt-1.5 w-full bg-white border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#647D6C]/20 focus:border-[#647D6C]";

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => api.get("/users").then((r) => setTeam(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/users/${removing.id}`);
      toast.success(`${removing.name} removed`);
      setRemoving(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight">Team</h1>
          <p className="text-sm mt-1" style={{ color: "#6A736D" }}>Add, edit, and manage the people in your workspace.</p>
        </div>
        <MemberDialog open={addOpen} setOpen={setAddOpen} onDone={load} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {team.map((t) => {
          const pct = t.annual_allowance ? Math.min(100, (t.used / t.annual_allowance) * 100) : 0;
          const isSelf = t.id === user?.id;
          return (
            <div key={t.id} className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }} data-testid={`member-${t.id}`}>
              <div className="flex items-center gap-3">
                <Avatar className="w-11 h-11 border" style={{ borderColor: "#EAE8E1" }}>
                  {t.avatar_url && <AvatarImage src={t.avatar_url} alt={t.name} />}
                  <AvatarFallback style={{ backgroundColor: "#647D6C", color: "white" }}>{initials(t.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{t.name}</p>
                    {t.role === "manager" && (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border" style={{ backgroundColor: "#E9F0EB", color: "#3D624A", borderColor: "#C4DFCD" }}>
                        <ShieldCheck className="w-3 h-3" /> Manager
                      </span>
                    )}
                  </div>
                  <p className="text-xs truncate" style={{ color: "#6A736D" }}>{t.email}</p>
                  {t.job_title && <p className="text-xs" style={{ color: "#6A736D" }}>{t.job_title}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`edit-member-${t.id}`} onClick={() => setEditing(t)} className="grid place-items-center w-8 h-8 rounded-md border hover:bg-[#F4F3ED]" style={{ borderColor: "#EAE8E1" }} title="Edit">
                    <Pencil className="w-3.5 h-3.5" style={{ color: "#6A736D" }} />
                  </button>
                  {!isSelf && (
                    <button data-testid={`remove-member-${t.id}`} onClick={() => setRemoving(t)} className="grid place-items-center w-8 h-8 rounded-md border hover:bg-[#FAECEE]" style={{ borderColor: "#EAC6CC" }} title="Remove">
                      <Trash2 className="w-3.5 h-3.5" style={{ color: "#8C3B4A" }} />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs mb-1.5" style={{ color: "#6A736D" }}>
                  <span>Allowance</span>
                  <span className="font-medium" style={{ color: "#2C332E" }}>{t.remaining}/{t.annual_allowance} left</span>
                </div>
                <Progress value={pct} className="h-2" />
                <div className="flex items-center justify-between text-xs mt-2" style={{ color: "#6A736D" }}>
                  <span>{t.used} taken</span>
                  <span className="flex items-center gap-1"><CalendarOff className="w-3 h-3" /> {t.sick_days} sick days</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <MemberDialog open={!!editing} setOpen={(o) => !o && setEditing(null)} member={editing} onDone={() => { setEditing(null); load(); }} />
      )}

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove {removing?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes their account and all their leave records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remove">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-remove" onClick={remove} style={{ backgroundColor: "#8C3B4A" }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MemberDialog({ open, setOpen, member, onDone }) {
  const isEdit = !!member;
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", job_title: "", annual_allowance: 25 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) {
      setForm({ name: member.name, email: member.email, password: "", role: member.role, job_title: member.job_title || "", annual_allowance: member.annual_allowance });
    } else {
      setForm({ name: "", email: "", password: "", role: "employee", job_title: "", annual_allowance: 25 });
    }
  }, [member, open]);

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/users/${member.id}`, {
          name: form.name, role: form.role, job_title: form.job_title, annual_allowance: Number(form.annual_allowance),
        });
        toast.success("Member updated");
      } else {
        await api.post("/users", {
          name: form.name, email: form.email.trim().toLowerCase(), password: form.password,
          role: form.role, job_title: form.job_title, annual_allowance: Number(form.annual_allowance),
        });
        toast.success("Team member added");
      }
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const valid = isEdit ? form.name : form.name && form.email && form.password.length >= 6;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && (
        <DialogTrigger asChild>
          <button data-testid="add-member-button" className="flex items-center gap-2 text-white rounded-md px-4 py-2 font-medium transition-colors" style={{ backgroundColor: "#647D6C" }}>
            <UserPlus className="w-4 h-4" /> Add member
          </button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit member" : "Add team member"}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium">Full name</label>
            <input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} placeholder="Jane Doe" />
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <input data-testid="member-email" type="email" disabled={isEdit} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls + (isEdit ? " opacity-60" : "")} style={{ borderColor: "#EAE8E1" }} placeholder="jane@company.co" />
          </div>
          {!isEdit && (
            <div>
              <label className="text-sm font-medium">Temporary password</label>
              <input data-testid="member-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} placeholder="min 6 characters" />
              <p className="text-xs mt-1" style={{ color: "#6A736D" }}>Share this with them; they sign in with it.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role-trigger" className="mt-1.5" style={{ borderColor: "#EAE8E1" }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee" data-testid="role-employee">Employee</SelectItem>
                  <SelectItem value="manager" data-testid="role-manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Allowance (days)</label>
              <input data-testid="member-allowance" type="number" min="0" step="0.5" value={form.annual_allowance} onChange={(e) => setForm({ ...form, annual_allowance: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Job title (optional)</label>
            <input data-testid="member-job-title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} placeholder="e.g. Designer" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="save-member-button" onClick={submit} disabled={saving || !valid} className="text-white rounded-md px-4 py-2 font-medium disabled:opacity-50 flex items-center gap-2" style={{ backgroundColor: "#647D6C" }}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save changes" : "Add member"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
